// What each Content pill is built from, and the first missing link when Best Times reads empty.
//
// Sits AFTER the refresh trace on purpose: Connect estate says whether a provider can connect, the trace says
// what each vendor call answered, and this says what the page could derive from what landed. The trace alone
// cannot settle Facebook — its per-post view reads are one Graph batch that answers 200 with failed parts inside.
import { useState } from "react";
import { Button, Card, Tag } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import { useSocialsDebug, type SocialsCheckRow } from "../../app/hooks/useSocialsDebug.js";
import styles from "./SyncChainPanel.module.scss";

/** ok = Best Times is filling; off = not connected (a fact, not a fault); fail = a link is missing. */
function statusOf(row: SocialsCheckRow): "ok" | "warn" | "fail" {
  if (!row.connected) return "warn";
  return (row.bestTimes ?? 0) > 0 ? "ok" : "fail";
}

function when(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : "never";
}

function Detail({ row }: { row: SocialsCheckRow }) {
  const lines: [string, string, boolean][] = [
    ["provider", row.provider ?? "—", true],
    ["stats doc", row.docAsOf ? `landed ${when(row.docAsOf)}` : "none landed", !!row.docAsOf],
    ["last sync", `${row.syncStatus ?? "—"} · ran ${when(row.lastRunOn)} · landed ${when(row.lastLandOn)}`,
      row.syncStatus === "ok" || row.syncStatus === "no-change"],
    ...(row.syncError ? [["sync error", row.syncError, false] as [string, string, boolean]] : []),
    ...(row.blockedReason ? [["blocked", row.blockedReason, false] as [string, string, boolean]] : []),
    ["posts held", `${row.ownPosts} (${row.datedPosts} dated)`, (row.ownPosts ?? 0) > 0],
    ["with views", String(row.postsWithViews), (row.postsWithViews ?? 0) > 0],
    ["with likes/comments", `${row.postsWithEngagement}${row.engagementSupported ? "" : " (not reported by this platform)"}`,
      (row.postsWithEngagement ?? 0) > 0 || !row.engagementSupported],
    ["best times rows", String(row.bestTimes), (row.bestTimes ?? 0) > 0],
    ["top 5 rows", String(row.topPosts), (row.topPosts ?? 0) > 0],
    ["top creators", row.topCreatorsSupported ? String(row.topCreators) : "hidden — no source for this platform",
      !row.topCreatorsSupported || (row.topCreators ?? 0) > 0],
  ];
  return (
    <ol className={styles.calls}>
      {lines.map(([k, v, ok]) => (
        <li key={k} className={styles.call} data-ok={ok}>
          <span className={styles.method}>{k}</span>
          <span className={styles.url}>{v}</span>
        </li>
      ))}
    </ol>
  );
}

function PlatformRow({ row }: { row: SocialsCheckRow }) {
  const [open, setOpen] = useState(false);
  const status = statusOf(row);
  return (
    <li className={styles.row}>
      <button type="button" className={styles.rowHead} onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span className={styles.light} data-status={status} aria-hidden="true" />
        <span className={styles.provider}>{row.platform}</span>
        {/* The server's verdict names the FIRST missing link, so the answer is readable without expanding. */}
        <span className={styles.summary}>{row.bestTimesVerdict}</span>
        <Tag tone={status === "ok" ? "success" : status === "warn" ? "neutral" : "danger"} size="sm">
          {status === "ok" ? "filling" : status === "warn" ? "not connected" : "empty"}
        </Tag>
      </button>
      {open ? <Detail row={row} /> : null}
    </li>
  );
}

export function SocialsPanel() {
  const q = useSocialsDebug();

  if (q.isPending) return <Card>Loading the socials check…</Card>;
  if (q.error) return <Card>Could not load the socials check: {errorMessage(q.error, "the request failed")}</Card>;
  const rows = q.data?.platforms ?? [];

  return (
    <Card>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Socials check</h2>
          <p className={styles.subtitle}>
            What each Content pill was built from, and why Best times fills or not. Run a refresh above, then re-check.
          </p>
        </div>
        <Button onClick={() => void q.refetch()} loading={q.isFetching}>Re-check</Button>
      </div>
      <ul className={styles.rows}>
        {rows.map((row) => <PlatformRow key={row.platform} row={row} />)}
      </ul>
    </Card>
  );
}
